import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useNavigate } from "react-router-dom";
import "@/index.css";
import App from "@/App";
import LoginPage from "@/LoginPage";
import AuthCallback from "@/AuthCallback";
import { useState, useEffect } from "react";
import getApiUrl from "./utils/getApiUrl";

// Protected Route Component
function ProtectedRoute({ children }) {
  const [user, setUser] = useState(() => {
    // Initialize from localStorage synchronously to avoid flash
    const storedUser = localStorage.getItem('auth_user');
    const storedToken = localStorage.getItem('auth_token');
    if (storedUser && storedToken) {
      try { return JSON.parse(storedUser); } catch { return null; }
    }
    return null;
  });
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // Trust localStorage on mount - verify in background
    return !!(localStorage.getItem('auth_token') && localStorage.getItem('auth_user'));
  });
  const [checked, setChecked] = useState(false);
  const location = useLocation();

  // Verify session in background (only once on mount)
  useEffect(() => {
    // If user came from AuthCallback with fresh data, use it directly
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      setChecked(true);
      return;
    }

    const storedToken = localStorage.getItem('auth_token');
    if (!storedToken) {
      setIsAuthenticated(false);
      setChecked(true);
      return;
    }

    // Background verify - don't block rendering
    let cancelled = false;
    const verifyToken = async () => {
      try {
        const apiUrl = getApiUrl();
        const response = await fetch(`${apiUrl}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${storedToken}` }
        });
        if (cancelled) return;

        if (response.ok) {
          const userData = await response.json();
          localStorage.setItem('auth_user', JSON.stringify(userData));
          setUser(userData);
          setIsAuthenticated(true);
        } else if (response.status === 401) {
          // Only clear on explicit 401 (session truly expired)
          localStorage.removeItem('auth_token');
          localStorage.removeItem('auth_user');
          setUser(null);
          setIsAuthenticated(false);
        }
        // For other errors (500, network), keep current session - don't logout
      } catch (error) {
        // Network error - keep current session, don't logout
        console.warn('Auth verify failed (network), keeping session:', error.message);
      }
      if (!cancelled) setChecked(true);
    };
    verifyToken();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Not authenticated and no token in localStorage
  if (!isAuthenticated && (checked || !localStorage.getItem('auth_token'))) {
    return <Navigate to="/login" replace />;
  }

  // Loading only on very first check when no localStorage data exists
  if (!isAuthenticated && !checked) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Pass user to children
  return React.cloneElement(children, { authUser: user });
}

import WidgetEmbedPage from "./pages/WidgetEmbedPage";
import PricingPage from "./pages/PricingPage";

// App Router - Handle session_id before ProtectedRoute
function AppRouter() {
  const location = useLocation();
  const navigate = useNavigate();

  // Check for auth_token in URL (redirect from preview after OAuth)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const authToken = params.get('auth_token');
    if (authToken) {
      // Store the token and fetch user data
      localStorage.setItem('auth_token', authToken);
      
      // Fetch user data with this token
      const fetchUser = async () => {
        try {
          const apiUrl = getApiUrl();
          const response = await fetch(`${apiUrl}/api/auth/me`, {
            headers: { 'Authorization': `Bearer ${authToken}` }
          });
          if (response.ok) {
            const userData = await response.json();
            localStorage.setItem('auth_user', JSON.stringify(userData));
          }
        } catch (e) {
          console.error('Error fetching user:', e);
        }
        // Remove token from URL and navigate to home
        navigate('/', { replace: true });
      };
      fetchUser();
      return;
    }
  }, [location.search, navigate]);

  // Check URL fragment for session_id SYNCHRONOUSLY during render
  // This prevents race conditions with ProtectedRoute
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // DEVELOPMENT MODE: Skip authentication on preview URL
  const isPreview = window.location.hostname.includes('preview.emergentagent.com');
  
  // In preview mode, we still need to use the real auth
  // so that API calls work correctly with tenancy
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/widget/:widgetId" element={<WidgetEmbedPage />} />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <App />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <BrowserRouter>
      <AppRouter />
    </BrowserRouter>
  </React.StrictMode>,
);

